import { Link, useParams, useSearchParams } from "react-router-dom";
import { VEHICLE_TYPE_LABELS } from "@garage/shared";
import { useServices } from "../api/services";
import { useVehicle } from "../api/vehicles";
import { ServiceList } from "../components/ServiceList";
import { ServiceFilters, type Filters } from "../components/ServiceFilters";
import { formatINR } from "@garage/shared";
import { ExpiryPill } from "../components/ExpiryPill";
import { Alert, Button, Card, Spinner } from "../components/ui";
import { formatDate, formatKm } from "../lib/format";

export default function VehiclePage() {
  const { id = "" } = useParams();
  const { data, isPending, error } = useVehicle(id);

  if (isPending) return <Spinner label="Loading vehicle" />;
  if (error) return <Alert>{(error as Error).message}</Alert>;

  const v = data.vehicle;
  const title =
    v.nickname || [v.make, v.model].filter(Boolean).join(" ") || v.regNumber;

  return (
    <div>
      <Link to="/" className="text-sm text-ink-3 hover:text-ink">
        ← Garage
      </Link>

      <div className="mt-3 flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 font-mono text-sm tracking-wider text-ink-3">
            {v.regNumber}
            <span className="mx-2 opacity-40">·</span>
            {VEHICLE_TYPE_LABELS[v.type]}
            {v.variant ? ` · ${v.variant}` : ""}
            {v.fuel ? ` · ${v.fuel.toLowerCase()}` : ""}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link to={`/vehicles/${v.id}/edit`}>
            <Button variant="ghost">Edit</Button>
          </Link>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <ExpiryPill label="PUC" date={v.pucExpiry} alwaysShow />
        <ExpiryPill label="Insurance" date={v.insuranceExpiry} alwaysShow />
        {v.type === "COMMERCIAL" && (
          <ExpiryPill label="Fitness" date={v.fitnessExpiry} alwaysShow />
        )}
      </div>

      <Card className="mt-6 p-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <Detail label="Odometer" value={formatKm(v.odometerKm)} />
          <Detail label="Year" value={v.year ? String(v.year) : "—"} />
          <Detail label="Bought" value={formatDate(v.purchaseDate)} />
          <Detail label="Insurer" value={v.insurer ?? "—"} />
        </dl>
      </Card>

      <ServiceSection vehicleId={v.id} />
    </div>
  );
}

function ServiceSection({ vehicleId }: { vehicleId: string }) {
  // Filters live in the URL, so a filtered view survives a refresh, works
  // with the back button, and can be sent to someone.
  const [params, setParams] = useSearchParams();
  const filters: Filters = Object.fromEntries(params.entries());
  const { data, isPending, error } = useServices(vehicleId, filters);

  const setFilters = (next: Filters) =>
    setParams(
      Object.entries(next).filter(([, v]) => v) as [string, string][],
      { replace: true },
    );

  const page = data?.page;

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-lg font-medium">Service history</h2>
        {data && (
          // Totals describe the whole filtered set, not the visible page.
          <p className="font-mono text-sm text-ink-3 tabular-nums">
            {data.totals.count} {data.totals.count === 1 ? "service" : "services"} ·{" "}
            {formatINR(data.totals.cost)}
            {data.totals.count > 1 && ` · ${formatINR(data.totals.avgCost)} avg`}
          </p>
        )}
        <Link to={`/vehicles/${vehicleId}/services/new`} className="ml-auto">
          <Button>Log a service</Button>
        </Link>
      </div>

      <div className="mt-4">
        <ServiceFilters
          filters={filters}
          facets={data?.facets}
          onChange={setFilters}
        />
      </div>

      <div className="mt-4">
        {isPending && <Spinner label="Loading services" />}
        {error && <Alert>{(error as Error).message}</Alert>}
        {data && data.items.length === 0 && data.totals.count === 0 && params.size > 0 ? (
          <Card className="p-8 text-center">
            <p className="text-ink-2">Nothing matches those filters.</p>
            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => setFilters({})}
            >
              Clear filters
            </Button>
          </Card>
        ) : (
          data && <ServiceList rows={data.items} vehicleId={vehicleId} />
        )}
      </div>

      {page && page.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="ghost"
            className="!py-1.5 text-sm"
            disabled={page.number <= 1}
            onClick={() => setFilters({ ...filters, page: String(page.number - 1) })}
          >
            Previous
          </Button>
          <span className="font-mono text-sm text-ink-3">
            {page.number} / {page.pages}
          </span>
          <Button
            variant="ghost"
            className="!py-1.5 text-sm"
            disabled={page.number >= page.pages}
            onClick={() => setFilters({ ...filters, page: String(page.number + 1) })}
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-1 tabular-nums">{value}</dd>
    </div>
  );
}
